import { useId, useRef } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";

import "./Abas.css";

/** Uma aba — e o **endereço** dela, que é o que a torna diferente de um botão. */
export interface Aba {
  /** Chave estável. É o que volta em {@link Props.aoEscolher} e o que vai na URL. */
  id: string;
  rotulo: string;
  /**
   * O endereço próprio da aba.
   *
   * <p>É `href` de verdade, e não adorno: sem ele não há como mandar o link de
   * uma aba específica, abrir numa janela nova nem recarregar a página sem
   * perder onde se estava. Um conjunto de abas cujo endereço não muda é um
   * conjunto de botões trocando `div`.
   */
  href: string;
  /** A contagem miúda ao lado do rótulo — `20` volumes, `4` sem etiqueta. */
  nota?: string | undefined;
}

interface Props {
  /** Nomeia o conjunto para quem ouve a tela — "Seções do lote". */
  rotulo: string;
  abas: readonly Aba[];
  /** O `id` da aba aberta. */
  atual: string;
  /**
   * Trocar de aba é **navegar**. O componente não conhece roteador — quem o
   * conhece é a tela, e é ela quem decide se isso é `navigate`, `pushState` ou
   * outra coisa.
   */
  aoEscolher(id: string): void;
  /** O conteúdo da aba aberta — vira o `tabpanel`. */
  children: ReactNode;
}

/**
 * Abas de verdade: `tablist`, setas, `Home`/`End` — e **um endereço por aba**.
 *
 * <p>Nasceu no lote (#36), onde resumo, volumes e etiquetas eram três páginas
 * separadas com ida e volta pela lista. Mora em {@code packages/ui} pelo motivo
 * de sempre: "seções de um registro" vai aparecer de novo — o fornecedor tem
 * qualificação e uso, o recipiente tem ficha e histórico —, e dois conjuntos de
 * abas com formas ligeiramente diferentes para o mesmo gesto é como uma
 * interface passa a induzir erro.
 *
 * <h2>Por que `&lt;a href&gt;` e não `&lt;button&gt;`</h2>
 *
 * <p>Porque **cada aba tem endereço**. Um `&lt;button&gt;` não se copia, não se
 * abre com <kbd>Ctrl</kbd>+clique, não sobrevive ao recarregar e não se manda
 * para alguém. Aqui o clique simples é interceptado (a tela navega sem
 * recarregar), e o clique com modificador — <kbd>Ctrl</kbd>, <kbd>Cmd</kbd>,
 * <kbd>Shift</kbd>, botão do meio — passa direto para o navegador. O endereço é
 * real; o `preventDefault` é o atalho, não a verdade.
 *
 * <p>`role="tab"` sobre um link é legítimo (WAI-ARIA APG) e é o que dá as duas
 * coisas ao mesmo tempo: a semântica de aba para quem ouve, e o endereço para
 * quem compartilha.
 *
 * <h2>O teclado é o que separa abas de `div` que troca</h2>
 *
 * <ul>
 *   <li><kbd>←</kbd> / <kbd>→</kbd> percorrem, e <b>dão a volta</b>: da última
 *       para a primeira e vice-versa;</li>
 *   <li><kbd>Home</kbd> / <kbd>End</kbd> vão para a primeira e a última;</li>
 *   <li><kbd>Tab</kbd> <b>sai do conjunto</b> em vez de percorrê-lo — só a aba
 *       aberta está no `tab order` (<i>roving tabindex</i>). Sem isso, tabular
 *       numa tela de N abas custa N paradas antes de chegar ao conteúdo.</li>
 * </ul>
 *
 * <p><b>Ativação automática</b>: mover o foco abre a aba. É o padrão
 * recomendado quando trocar de painel é barato, e aqui é — cada painel carrega
 * o que precisa e o cabeçalho do registro não recarrega. A alternativa (mover o
 * foco e exigir <kbd>Enter</kbd>) existe para painel caro, e paga com um gesto a
 * mais em todo uso normal.
 *
 * <h2>⚠️ A lista de abas NÃO vai para o papel</h2>
 *
 * <p>A garantia é do componente, em `ui.css`, e não de cada tela que o usa:
 * `@media print { .ui-abas { display: none } }`. Deixá-la para a tela é a mesma
 * lista de ocultos que a folha de etiquetas já trocou por lista de permitidos —
 * ela falha na direção perigosa, porque ninguém confere o que saiu da
 * impressora.
 */
export function Abas({ rotulo, abas, atual, aoEscolher, children }: Props) {
  const prefixo = useId();
  const refs = useRef<(HTMLAnchorElement | null)[]>([]);

  const idDaAba = (id: string) => `${prefixo}-aba-${id}`;
  const idDoPainel = (id: string) => `${prefixo}-painel-${id}`;

  /*
   * A aba aberta pode não estar na lista — endereço digitado à mão, aba que
   * saiu do sistema. Nesse caso NENHUMA está marcada, e é melhor assim: marcar
   * a primeira faria a tela afirmar que se está numa seção em que não se está.
   */
  const indiceAtual = abas.findIndex((a) => a.id === atual);

  function aoTeclar(evento: KeyboardEvent<HTMLAnchorElement>, indice: number) {
    const ultimo = abas.length - 1;
    let alvo: number | null = null;

    if (evento.key === "ArrowRight") alvo = indice === ultimo ? 0 : indice + 1;
    else if (evento.key === "ArrowLeft") alvo = indice === 0 ? ultimo : indice - 1;
    else if (evento.key === "Home") alvo = 0;
    else if (evento.key === "End") alvo = ultimo;

    if (alvo === null) return;
    // Sem isto, `Home` e `End` rolam a página junto — o foco vai para a aba e a
    // tela vai para outro lugar.
    evento.preventDefault();
    refs.current[alvo]?.focus();
    aoEscolher(abas[alvo]!.id);
  }

  function aoClicar(evento: MouseEvent<HTMLAnchorElement>, id: string) {
    /*
     * Clique com modificador é do NAVEGADOR: abrir noutra aba, noutra janela,
     * copiar o endereço. Interceptar tudo transformaria o `href` numa mentira —
     * ele estaria escrito e não faria o que promete.
     */
    if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;
    if (evento.button !== 0) return;
    evento.preventDefault();
    aoEscolher(id);
  }

  return (
    <>
      {/* `role="tablist"` num `<nav>`: são links de navegação, e o `aria-label`
          é o que distingue este conjunto do menu do sistema para quem ouve. */}
      <nav className="ui-abas" aria-label={rotulo}>
        <div className="ui-abas__lista" role="tablist" aria-label={rotulo}>
          {abas.map((aba, indice) => {
            const aberta = aba.id === atual;
            return (
              <a
                key={aba.id}
                ref={(el) => {
                  refs.current[indice] = el;
                }}
                id={idDaAba(aba.id)}
                className={`ui-abas__aba${aberta ? " ui-abas__aba--atual" : ""}`}
                href={aba.href}
                role="tab"
                aria-selected={aberta}
                aria-controls={idDoPainel(aba.id)}
                /* Roving tabindex. Quando nenhuma casa (endereço estranho), a
                   PRIMEIRA fica alcançável — um conjunto inteiro fora do tab
                   order é um conjunto que o teclado não alcança mais. */
                tabIndex={aberta || (indiceAtual === -1 && indice === 0) ? 0 : -1}
                onClick={(e) => aoClicar(e, aba.id)}
                onKeyDown={(e) => aoTeclar(e, indice)}
              >
                <span className="ui-abas__rotulo">{aba.rotulo}</span>
                {aba.nota !== undefined && (
                  <span className="ui-abas__nota">{aba.nota}</span>
                )}
              </a>
            );
          })}
        </div>
      </nav>

      {/* O painel é rotulado pela aba que o abriu — é assim que o leitor de tela
          diz de qual seção é o conteúdo que acabou de aparecer.

          `tabIndex={0}`: o painel não tem controle focável garantido (o resumo
          é só texto), e um painel sem nada focável dentro é conteúdo que o
          teclado alcança pela rolagem e não pelo foco. É o que a APG pede. */}
      <div
        className="ui-abas__painel"
        id={idDoPainel(atual)}
        role="tabpanel"
        aria-labelledby={indiceAtual === -1 ? undefined : idDaAba(atual)}
        tabIndex={0}
      >
        {children}
      </div>
    </>
  );
}
